/**
 * Auth feature flags.
 *
 * ENFORCE_WEB_APP_CONTEXT — when true, the client pre-blocks internal/staff
 * (admin-group) Cognito accounts from signing in on the customer SPA; they must
 * use the internal app hosted in web-admin. Now that the configurator ships on
 * web-admin this defaults to true (mirrors the backend gate svc-02
 * `AUTH_ENFORCE_WEB_APP_CONTEXT`). Set
 * `VITE_AUTH_ENFORCE_WEB_APP_CONTEXT=false` to relax it (e.g. local testing).
 */
import { webCommonHost } from "./host";
import { runtimeEnv } from "./runtime-env";

function envFlag(value: unknown, fallback: boolean): boolean {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value).toLowerCase() === "true";
}

export const ENFORCE_WEB_APP_CONTEXT = envFlag(
  runtimeEnv("VITE_AUTH_ENFORCE_WEB_APP_CONTEXT"),
  true,
);

/**
 * AUTH_MODE — which sign-in front door this SPA uses.
 *
 * - `cognito` (default): the app-local Amplify email-OTP flow against the
 *   Configurator's own Cognito pool. Everything shipped before this flag.
 * - `oidc`: the platform identity issuer. /login redirects to the issuer's
 *   /authorize (PKCE), /auth/callback redeems the code and runs the SAME
 *   svc-02 session exchange with the issuer id_token (svc-02 routes on `iss`).
 *
 * Set `VITE_AUTH_MODE=oidc` plus `VITE_OIDC_ISSUER_URL` and
 * `VITE_OIDC_CLIENT_ID` to switch. Any other value, or unset, is `cognito`, so
 * an environment that has not been given the variable keeps its current
 * sign-in unchanged.
 */
export type AuthMode = "cognito" | "oidc";

export function parseAuthMode(value: unknown): AuthMode {
  return String(value ?? "")
    .trim()
    .toLowerCase() === "oidc"
    ? "oidc"
    : "cognito";
}

export const AUTH_MODE: AuthMode = parseAuthMode(runtimeEnv("VITE_AUTH_MODE"));

export const OIDC_ENABLED = AUTH_MODE === "oidc";

/**
 * Where "Register" goes.
 *
 * Under `oidc` the Configurator hosts no sign-up form of its own: accounts are
 * created at the platform sign-up door, `VITE_OIDC_REGISTER_URL` (for example
 * `https://signup.identity.<env>.pemnet.ai/?app=configurator&client=<client>`).
 * The app-local Amplify form must not be offered in that mode — it signs up
 * against the Configurator's own pool, which the session exchange no longer
 * accepts, so the user ends at `user-not-registered`. With no door configured
 * there is nothing to offer (`null`), and the callers hide Register.
 * Cognito mode keeps the in-app `/register` route unchanged.
 */
export type RegisterDestination =
  | { kind: "route"; path: "/register" }
  | { kind: "external"; url: string };

export function resolveRegisterDestination(
  mode: AuthMode,
  registerUrl: unknown,
  /** Host policy when oidc mode has no https door. Defaults to the customer
   *  surface's behaviour: offer nothing. */
  fallback: "none" | "route" = "none",
): RegisterDestination | null {
  if (mode !== "oidc") return { kind: "route", path: "/register" };
  const url = String(registerUrl ?? "").trim();
  if (/^https:\/\//i.test(url)) return { kind: "external", url };
  return fallback === "route" ? { kind: "route", path: "/register" } : null;
}

/**
 * A FUNCTION, not a constant: the no-door fallback is per-surface and comes
 * from the host, which is set at boot — after module evaluation. Call it from
 * a component or handler, never at module scope.
 */
export function registerDestination(): RegisterDestination | null {
  return resolveRegisterDestination(
    AUTH_MODE,
    runtimeEnv("VITE_OIDC_REGISTER_URL"),
    webCommonHost().registerFallback,
  );
}

/**
 * OIDC_REALM — which realm this surface requests on `/authorize`.
 * `VITE_OIDC_REALM` is trimmed and lowercased; anything other than `external`
 * or `internal` (unset included)
 * resolves to `undefined`, and oidc-auth.ts omits the `realm` param entirely
 * in that case so the query string is unchanged from before this flag existed.
 */
export function resolveOidcRealm(value: unknown): "external" | "internal" | undefined {
  const realm = String(value ?? "")
    .trim()
    .toLowerCase();
  return realm === "external" || realm === "internal" ? realm : undefined;
}

export const OIDC_REALM = resolveOidcRealm(runtimeEnv("VITE_OIDC_REALM"));

/**
 * AUTH_COOKIE_SESSION — when true, the SPA uses the cookie-backed Penn session
 * (ADR-0036): it calls `/v1/auth/refresh` at boot, on a timer, on
 * `visibilitychange` and as the first recovery attempt on a 401.
 *
 * Defaults to FALSE so a deploy carrying this code behaves exactly as before.
 * It is the client half of a two-key switch: the server's
 * `AUTH_REFRESH_COOKIE_MODE` decides whether a cookie is issued at all, so
 * turning this on without the server flag simply produces `401 no-session` on
 * the boot refresh and falls back to the existing Cognito re-exchange.
 */
export const AUTH_COOKIE_SESSION = envFlag(
  runtimeEnv("VITE_AUTH_COOKIE_SESSION"),
  // Defaults ON so a deploy to staging/UAT/prod has cookie sessions live with
  // no per-environment opt-in. Set VITE_AUTH_COOKIE_SESSION=false to roll back.
  true,
);
